// Écran 6 — Sources : quels logiciels Ekera lit, par quel connecteur, avec quel compte, quand a eu lieu la dernière
// lecture, ce qu'elle a apporté et ce qui a échoué. Parcours : liste, fiche d'une source, « Ajouter une source » et,
// en dernier onglet, les écritures dans les outils (ancien écran « Outils »).
// Règle : aucun secret ne transite par l'application. On déclare la RÉFÉRENCE d'un secret dans le coffre du client ;
// le collecteur sur site lit le secret dans le coffre. Jamais de suppression (désactivation avec motif).
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { PieceLue, SourceVue } from '../api/types';
import { Attendre, EtiquetteEtatSource } from '../composants/communs';
import { useEkera, usePieces } from '../contexte';
import { dateFr } from '../domaine/format';
import { EXPLICATION_ETAT_SOURCE, LIBELLE_ETAT_SOURCE, LIBELLE_TYPE_PIECE } from '../domaine/sources';
import { lienVers } from '../routeur';
import { FicheSource } from './SourceFiche';
import { AjouterSource } from './SourceAjout';
import { EcranOutils } from './Outils';

export type Onglet = 'sources' | 'ajouter' | 'ecritures';

export function EcranSources({ params }: { params: URLSearchParams }) {
  const { administrateur } = useEkera();
  const sid = params.get('source');
  const brut = params.get('onglet');
  const onglet: Onglet = brut === 'ajouter' || brut === 'ecritures' ? brut : 'sources';
  return (
    <div className="pile">
      <nav className="onglets" aria-label="Parcours des sources">
        <LienOnglet actif={onglet === 'sources'} href={lienVers('sources')}>
          Sources lues
        </LienOnglet>
        {administrateur && (
          <LienOnglet actif={onglet === 'ajouter'} href={lienVers('sources', { onglet: 'ajouter' })}>
            Ajouter une source
          </LienOnglet>
        )}
        <LienOnglet actif={onglet === 'ecritures'} href={lienVers('sources', { onglet: 'ecritures' })}>
          Écritures dans les outils
        </LienOnglet>
      </nav>
      {onglet === 'ecritures' && <EcranOutils />}
      {onglet === 'ajouter' && <AjouterSource />}
      {onglet === 'sources' && (sid ? <FicheSource sid={sid} /> : <ListeSources />)}
    </div>
  );
}

function LienOnglet({ actif, href, children }: { actif: boolean; href: string; children: ReactNode }) {
  return (
    <a href={href} aria-current={actif ? 'page' : undefined} className="onglet">
      {children}
    </a>
  );
}

function ListeSources() {
  const { client, cid, administrateur } = useEkera();
  const sources = useQuery({ queryKey: ['carte', cid, 'sources'], queryFn: () => client.sources(cid) });
  const pieces = usePieces();
  return (
    <>
      <p className="sous-titre">
        Les pièces qu’Ekera a lues pour dessiner la carte, et les logiciels que le collecteur d’Ekera lit chez vous ou dont
        vous déposez un export. Ekera ne détient aucun mot de passe : chaque source ne garde que la <b>référence</b> de son
        secret dans votre coffre. Pour un diagnostic, déposez vos documents et exports dans l’écran{' '}
        <a href={lienVers('depot')}>« Dépôt des documents »</a>.
      </p>
      {/* les pièces lues ne bloquent pas l'écran : une API plus ancienne n'a pas la route */}
      {pieces.data && <PiecesLues pieces={pieces.data} />}
      <Attendre etats={[sources]} quoi="des sources">
        {() => <TableauSources sources={sources.data ?? []} administrateur={administrateur} />}
      </Attendre>
    </>
  );
}

/** Les pièces lues par le diagnostic (T4) : titre, type, date, ce que la lecture et la carte en ont tiré. */
function PiecesLues({ pieces }: { pieces: PieceLue[] }) {
  if (pieces.length === 0) return null;
  const observations = pieces.reduce((n, p) => n + (p.observations ?? 0), 0);
  return (
    <section className="panneau" aria-labelledby="t-pieces" data-testid="pieces-lues">
      <div className="panneau-tete">
        <h2 id="t-pieces">Pièces lues pour le diagnostic ({pieces.length})</h2>
        <span className="indice num">{observations} observations citées</span>
      </div>
      <div className="tableau-defile">
        <table>
          <caption className="sr">Pièces lues par Ekera, avec leur date et ce qu’elles ont apporté</caption>
          <thead>
            <tr>
              <th scope="col">Pièce</th>
              <th scope="col">Type</th>
              <th scope="col">Date</th>
              <th scope="col" className="num">
                Observations
              </th>
              <th scope="col" className="num">
                Citées dans la carte
              </th>
            </tr>
          </thead>
          <tbody>
            {pieces.map((p) => (
              <tr key={p.id}>
                <th scope="row">{p.titre ?? p.id}</th>
                <td>{(p.type && LIBELLE_TYPE_PIECE[p.type]) ?? p.type ?? '—'}</td>
                <td>{p.date ? dateFr(p.date) : '—'}</td>
                <td className="num">{p.observations ?? '—'}</td>
                <td className="num">{p.citations}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="note">
        Chaque fait de la carte cite sa pièce mot pour mot. Ekera ne montre ici ni le chemin ni le contenu des pièces.
      </p>
    </section>
  );
}

function TableauSources({ sources, administrateur }: { sources: SourceVue[]; administrateur: boolean }) {
  if (sources.length === 0)
    return (
      <section className="panneau" aria-labelledby="t-vide">
        <h2 id="t-vide">Aucune source lue en continu</h2>
        <p>
          Cette carte ne reçoit encore aucune lecture automatique (connecteur ou export déposé).{' '}
          {administrateur ? (
            <>
              <a href={lienVers('sources', { onglet: 'ajouter' })}>Ajouter une source</a> pour commencer.
            </>
          ) : (
            'Une personne ayant le rôle « administration » peut en déclarer.'
          )}
        </p>
      </section>
    );
  const compte = (e: SourceVue['etat']) => sources.filter((s) => s.etat === e).length;
  return (
    <>
      <ul className="kpis" aria-label="Résumé des états" style={{ listStyle: 'none', padding: 0 }}>
        {(['a_jour', 'en_retard', 'en_erreur', 'jamais_lu', 'desactivee'] as const).map((e) => (
          <li key={e} className={`kpi ${e === 'en_erreur' && compte(e) > 0 ? 'alerte' : e === 'a_jour' ? 'bien' : ''}`}>
            <span className="l">{LIBELLE_ETAT_SOURCE[e]}</span>
            <span className="v">{compte(e)}</span>
            <span className="d">{EXPLICATION_ETAT_SOURCE[e]}</span>
          </li>
        ))}
      </ul>
      <section className="panneau" aria-labelledby="t-sources">
        <div className="panneau-tete">
          <h2 id="t-sources">Sources déclarées</h2>
          {administrateur && (
            <a className="btn petit principal" href={lienVers('sources', { onglet: 'ajouter' })}>
              Ajouter une source
            </a>
          )}
        </div>
        <div className="tableau-defile">
          <table>
            <caption className="sr">Sources lues par Ekera, avec l’état de leur dernière lecture</caption>
            <thead>
              <tr>
                <th scope="col">Logiciel</th>
                <th scope="col">État</th>
                <th scope="col">Dernière lecture</th>
                <th scope="col" className="num">
                  Objets apportés
                </th>
                <th scope="col" className="num">
                  Contradictions ouvertes
                </th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id}>
                  <th scope="row">
                    <a href={lienVers('sources', { source: s.id })}>{s.nom}</a>
                    <span className="note" style={{ display: 'block', fontWeight: 400 }}>
                      {s.mode === 'connecteur' ? 'Connecteur' : 'Export déposé'}
                      {s.compte ? ` · compte ${s.compte}` : ''}
                      {s.mode === 'connecteur' && (
                        <>
                          {' · '}
                          {s.collecteur ? `lue par ${s.collecteur.nom ?? s.collecteur.id}` : 'aucun collecteur : la source n’est lue par personne'}
                        </>
                      )}
                    </span>
                  </th>
                  <td>
                    <EtiquetteEtatSource etat={s.etat} />
                  </td>
                  <td>
                    {s.derniere_lecture ? (
                      <>
                        {dateFr(s.derniere_lecture.le)}
                        {s.derniere_lecture.statut === 'erreur' && <span className="note"> · en échec</span>}
                      </>
                    ) : (
                      <span className="note">Aucune lecture</span>
                    )}
                  </td>
                  <td className="num">{s.objets ?? '—'}</td>
                  <td className="num">{s.contradictions_ouvertes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note">
          Une source passe « en retard » au-delà d’un délai fixé par source (fréquence de lecture attendue, puis tolérance) ; il
          est indiqué sur sa fiche.
        </p>
      </section>
    </>
  );
}
